import { SITE_BASE_URL } from '../../../constants';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CSSProperties } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import { useBreathworks } from '@breath-lab/state';
import { useAuth } from '@breath-lab/state';
import { breathworkNoBgImageUrl, breathworkNoBgImageSrcSet } from '../../../domain/breathworkHelpers';
import { benefitChipLabel } from '../../../domain/breathworkDisplayHelpers';
import { getPrimaryName, getSecondaryName } from '../../../domain/breathworkNameDisplay';
import { useBreathworkNameDisplay } from '../../hooks/useBreathworkNameDisplay';
import { breathworkConfigurePath, slugToId, idToSlug, ROUTES } from '../../../routes';
import { PageMeta } from '../../seo/PageMeta';
import { JsonLd } from '../../seo/JsonLd';
import { getBreathworkSkeletonMeta } from '../../../seo/routes';
import { useQuickStart } from '../../../domain/quickStartHelpers';
import { useAccessGate } from '../../hooks/useAccessGate';
import { refreshCustomerInfo, getIsPremium } from '@breath-lab/state';
import Modal from '@ui/components/Modal/Modal';
import UpgradePanel from '../../components/UpgradePanel/UpgradePanel';
import InfoTag from '@ui/components/InfoTag/InfoTag';
import Button from '@ui/components/Button/Button';
import IconButton from '@ui/components/IconButton/IconButton';
import AccessBadge from '@ui/components/AccessBadge/AccessBadge';
import styles from './BreathworkDetailPage.module.css';
import { logEvent } from '../../../infra/firebase/analytics';
import { AnalyticsEvent, AnalyticsParam } from '../../../domain/analytics';
import { useShare, captureReferralParam } from '../../../domain/sharing';
import { ShareIcon } from '../../components/ShareIcon';

const truncateDescription = (s: string, max = 152) =>
  s.length <= max ? s : s.slice(0, s.lastIndexOf(' ', max)) + '…'

export default function BreathworkDetailPage() {
  const { t } = useTranslation('breathworks');
  const { t: tCommon } = useTranslation('common');
  const { id: slug } = useParams<{ id: string }>();
  const id = slugToId(slug ?? '');
  const location = useLocation();
  const { breathworks, activities, benefits, isLoading, load, loadPublic, freeIds, paywallEnabled } = useBreathworks();
  const { user, isAuthenticated, openLoginModal } = useAuth();
  const display = useBreathworkNameDisplay();

  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { quickStart, loadingId } = useQuickStart({ onTokenFailed: () => setUpgradeOpen(true) });
  const { isBreathworkAccessible } = useAccessGate();
  const [upgradeLoading, setUpgradeLoading] = useState(false);

  const heroCtaRef = useRef<HTMLDivElement>(null);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      load();
    } else {
      loadPublic();
    }
  }, [isAuthenticated, load, loadPublic]);

  // Show sticky bar once the hero CTA scrolls out of view
  useEffect(() => {
    if (isLoading) return;
    const el = heroCtaRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowStickyBar(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isLoading]);

  const breathwork = breathworks.find((b) => b.id === id);

  useEffect(() => {
    if (!isLoading && breathwork) {
      logEvent(AnalyticsEvent.screenView, {
        [AnalyticsParam.screenName]: 'breathwork_detail',
        [AnalyticsParam.breathworkId]: id,
      });
      logEvent(AnalyticsEvent.viewBreathwork, { [AnalyticsParam.breathworkId]: id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, breathwork?.id]);

const searchParams = new URLSearchParams(location.search);
  const refParam = searchParams.get('ref');
  useEffect(() => {
    if (!refParam) return;
    captureReferralParam(refParam, user?.uid ?? null);
    if (breathwork?.id) {
      logEvent(AnalyticsEvent.referralLinkVisit, {
        [AnalyticsParam.referrerUid]: refParam,
        [AnalyticsParam.contentType]: 'breathwork',
        [AnalyticsParam.id]: breathwork.id,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refParam]);

  const handleShare = useShare(
    'detail',
    'breathwork',
    idToSlug(breathwork?.id ?? ''),
    breathwork ? getPrimaryName(breathwork.name, display) : '',
    user?.uid ?? null,
  );

  if (isLoading || breathworks.length === 0) {
    const { name: displayName, description: displayDesc, steps: displaySteps } = getBreathworkSkeletonMeta(id)
    return (
      <div className={styles.page}>
        <PageMeta
          title={displayName}
          description={displayDesc}
          path={`/breathworks/${slug}`}
          image={`${SITE_BASE_URL}/og-breathwork-${slug}.png`}
          ogType="article"
        />
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'HowTo',
            name: displayName,
            description: displayDesc,
            url: `${SITE_BASE_URL}/breathworks/${slug}`,
            image: `${SITE_BASE_URL}${breathworkNoBgImageUrl(id) ?? '/app_icon.png'}`,
            step: displaySteps.map((s, i) => ({
              '@type': 'HowToStep',
              position: i + 1,
              name: s.step,
              text: s.description,
            })),
          }}
        />
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_BASE_URL + '/' },
              { '@type': 'ListItem', position: 2, name: 'Breathworks', item: SITE_BASE_URL + '/breathworks' },
              { '@type': 'ListItem', position: 3, name: displayName },
            ],
          }}
        />
        <div className={styles.hero}>
          <div className={styles.skeletonImage} />
          <div className={styles.skeletonTagsPanel}>
            <h1 className={styles.title}>{displayName}</h1>
            <div className={styles.skeletonLine} style={{ width: '40%', height: '1rem', marginBottom: '0.25rem' }} />
            <div className={styles.skeletonLine} style={{ width: '50%', height: '0.75rem' }} />
            <div className={styles.skeletonTagRow}>
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} style={{ width: '5rem' }} />
              <div className={styles.skeletonTag} style={{ width: '4rem' }} />
            </div>
            <div className={styles.skeletonLine} style={{ width: '40%', height: '0.75rem', marginTop: '0.5rem' }} />
            <div className={styles.skeletonTagRow}>
              <div className={styles.skeletonTag} />
              <div className={styles.skeletonTag} style={{ width: '4rem' }} />
            </div>
          </div>
        </div>
        <div className={styles.skeletonContent}>
          <p className={styles.description}>{displayDesc}</p>
          {displaySteps.length > 0 && (
            <ol className={styles.skeletonSteps}>
              {displaySteps.map((s) => (
                <li key={s.step} className={styles.skeletonStep}>
                  <strong className={styles.skeletonStepName}>{s.step}</strong>
                  <p className={styles.skeletonStepDesc}>{s.description}</p>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    );
  }

  if (!breathwork) {
    return (
      <div className={styles.page}>
        <div className={styles.notFound}>
          <p>{t('detail.notFound')}</p>
          <Button variant="ghost" as={Link} to={ROUTES.breathworks}>{t('detail.browseAll')}</Button>
        </div>
      </div>
    );
  }

  const isPro = paywallEnabled && !freeIds.includes(breathwork.id);

  const handleStart = async () => {
    if (!isBreathworkAccessible(breathwork.id)) {
      logEvent(AnalyticsEvent.premiumGateShown, { [AnalyticsParam.breathworkId]: id });
      setUpgradeLoading(true);
      await refreshCustomerInfo();
      setUpgradeLoading(false);
      // Re-check with fresh RC data — force skips the stale isBreathworkAccessible closure
      if (getIsPremium()) {
        logEvent(AnalyticsEvent.practicePranayamaTapped, { [AnalyticsParam.breathworkId]: id });
        quickStart(breathwork, { force: true });
        return;
      }
      setUpgradeOpen(true);
      return;
    }
    logEvent(AnalyticsEvent.practicePranayamaTapped, { [AnalyticsParam.breathworkId]: id });
    quickStart(breathwork);
  };

  const imageUrl = breathworkNoBgImageUrl(breathwork.id);
  const imageSrcSet = breathworkNoBgImageSrcSet(breathwork.id);

  const bwBenefits = breathwork.benefitsIds
    .map((bid) => benefits.find((b) => b.id === bid))
    .filter((b): b is NonNullable<typeof b> => b !== undefined);

  const bwActivities = breathwork.activitiesIds
    .map((aid) => activities.find((a) => a.id === aid))
    .filter((a): a is NonNullable<typeof a> => a !== undefined);
  const tagSections = (
    <>
      {breathwork.difficultyLevel && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('detail.difficulty')}</h2>
          <div className={styles.tags}>
            <InfoTag>
              {breathwork.difficultyLevel.toLowerCase()}
            </InfoTag>
          </div>
        </section>
      )}

      {bwBenefits.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('detail.benefits')}</h2>
          <div className={styles.tags}>
            {bwBenefits.map((b) => (
              <InfoTag
                key={b.id}
                leading={b.emoji ? <span>{b.emoji}</span> : undefined}
              >
                {benefitChipLabel(b)}
              </InfoTag>
            ))}
          </div>
        </section>
      )}

      {bwActivities.length > 0 && (
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('detail.bestFor')}</h2>
          <div className={styles.tags}>
            {bwActivities.map((a) => (
              <InfoTag
                key={a.id}
                leading={a.emoji ? <span>{a.emoji}</span> : undefined}
              >
                {a.name.en.toLowerCase()}
              </InfoTag>
            ))}
          </div>
        </section>
      )}
    </>
  );

  return (
    <div className={styles.page} style={breathwork.configurationUi != null ? { paddingBottom: '6rem' } : undefined}>
      <PageMeta
        title={breathwork.name.en}
        description={truncateDescription(breathwork.description.en)}
        path={`/breathworks/${idToSlug(breathwork.id)}`}
        image={`${SITE_BASE_URL}/og-breathwork-${idToSlug(breathwork.id)}.png`}
        ogType="article"
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: breathwork.name.en,
          description: breathwork.description.en,
          image: `${SITE_BASE_URL}${breathworkNoBgImageUrl(breathwork.id) ?? '/app_icon.png'}`,
          step: breathwork.teachingSteps.map((s, i) => ({
            '@type': 'HowToStep',
            position: i + 1,
            name: s.step.en,
            text: s.description.en,
          })),
          ...(breathwork.goal.en ? { result: breathwork.goal.en } : {}),
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_BASE_URL + '/' },
            { '@type': 'ListItem', position: 2, name: 'Breathworks', item: SITE_BASE_URL + '/breathworks' },
            { '@type': 'ListItem', position: 3, name: breathwork.name.en },
          ],
        }}
      />
      <div className={styles.hero}>
        {imageUrl && (
          <div
            className={styles.imageWrapper}
            style={breathwork.backgroundColor ? { '--img-bg': breathwork.backgroundColor } as CSSProperties : undefined}
          >
            <img
              src={imageUrl}
              srcSet={imageSrcSet ?? undefined}
              alt={t('detail.imageAlt', { name: breathwork.name.en })}
              className={styles.image}
            />
          </div>
        )}

        <div className={styles.heroInfo}>
          {paywallEnabled && isPro && (
            <div className={styles.badgeRow}>
              <AccessBadge variant="premium">
                {t('badges.pro')}
              </AccessBadge>
            </div>
          )}
          <div className={styles.header}>
            <div className={styles.headerTitleGroup}>
              <h1 className={styles.title}>
                {[getPrimaryName(breathwork.name, display), getSecondaryName(breathwork.name, display)].filter(Boolean).join(' — ')}
              </h1>
            </div>
            <IconButton variant="bordered" label={tCommon('share.ariaLabel')} onClick={handleShare}>
              <ShareIcon />
            </IconButton>
          </div>

          {tagSections}

          {(isAuthenticated ? breathwork.configurationUi != null : true) && (
            <div ref={heroCtaRef} className={styles.heroCtas}>
              {isAuthenticated ? (
                <>
                  <Button
                    variant="primary"
                    className={styles.startAction}
                    onClick={handleStart}
                    disabled={loadingId === breathwork.id || upgradeLoading}
                  >
                    {loadingId === breathwork.id || upgradeLoading ? t('detail.loading') : t('detail.startSession')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="md"
                    as={Link}
                    to={breathworkConfigurePath(breathwork.id)}
                  >
                    {t('detail.customizeSession')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  className={styles.startAction}
                  onClick={() => openLoginModal(location.pathname)}
                >
                  {t('detail.signInToStart')}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className={styles.inner}>

        <div className={styles.mainColumn}>
          {breathwork.goal.en && (
            <section className={styles.section}>
              <h2 className={styles.contentSectionTitle}>{t('detail.goal')}</h2>
              <p className={styles.body}>{breathwork.goal.en}</p>
            </section>
          )}

          {breathwork.description.en && (
            <section className={styles.section}>
              <h2 className={styles.contentSectionTitle}>{t('detail.overview')}</h2>
              <p className={styles.description}>{breathwork.description.en}</p>
            </section>
          )}

          <>
              {breathwork.whenToUse.length > 0 && (
                <section className={styles.section}>
                  <h2 className={styles.contentSectionTitle}>{t('detail.whenToUse')}</h2>
                  <ul className={styles.list}>
                    {breathwork.whenToUse.map((w, i) => (
                      <li key={i} className={styles.listItem}>{w.en}</li>
                    ))}
                  </ul>
                </section>
              )}

              {breathwork.teachingSteps.length > 0 && (
                <section className={styles.section}>
                  <h2 className={styles.contentSectionTitle}>{t('detail.howToPractice')}</h2>
                  <ol className={styles.steps}>
                    {breathwork.teachingSteps.map((step, i) => (
                      <li key={i} className={styles.step}>
                        <span className={styles.stepTitle}>{step.step.en}</span>
                        {step.description.en && (
                          <p className={styles.stepDescription}>{step.description.en}</p>
                        )}
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              {breathwork.cautions.length > 0 && (
                <section className={styles.section}>
                  <h2 className={styles.contentSectionTitle}>{t('detail.cautions')}</h2>
                  <ul className={styles.list}>
                    {breathwork.cautions.map((c, i) => (
                      <li key={i} className={styles.listItem}>{c.en}</li>
                    ))}
                  </ul>
                </section>
              )}

              {breathwork.supportingDocs && breathwork.supportingDocs.studies.length > 0 && (
                <section className={styles.section}>
                  <h2 className={styles.contentSectionTitle}>{t('detail.research')}</h2>
                  {breathwork.supportingDocs.primaryBenefits.en && (
                    <p className={styles.body}>{breathwork.supportingDocs.primaryBenefits.en}</p>
                  )}
                  <div className={styles.studies}>
                    {breathwork.supportingDocs.studies.map((study, i) =>
                      <a
                        key={i}
                        href={study.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.studyCard}
                      >
                        <svg className={styles.studyIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                          <line x1="16" y1="13" x2="8" y2="13" />
                          <line x1="16" y1="17" x2="8" y2="17" />
                          <polyline points="10 9 9 9 8 9" />
                        </svg>
                        <div className={styles.studyBody}>
                          <p className={styles.studyTitle}>{study.title}</p>
                          <p className={styles.studyMeta}>
                            {study.authors} · {study.journal} · {study.year}
                          </p>
                        </div>
                        <svg className={styles.studyExternalIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                      </a>
                    )}
                  </div>
                </section>
              )}
            </>
        </div>
      </div>

      {upgradeOpen && (
        <Modal onClose={() => setUpgradeOpen(false)} wide>
          <UpgradePanel onComplete={() => setUpgradeOpen(false)} />
        </Modal>
      )}

      {showStickyBar && breathwork.configurationUi != null && (
        <div className={styles.stickyBar}>
          <div className={styles.stickyBarInner}>
            <span className={styles.stickyBarName}>
              {getPrimaryName(breathwork.name, display)}
            </span>
            <div className={styles.stickyBarActions}>
              {isAuthenticated ? (
                <>
                  <Button
                    variant="ghost"
                    size="md"
                    as={Link}
                    to={breathworkConfigurePath(breathwork.id)}
                  >
                    {t('detail.customize')}
                  </Button>
                  <Button
                    variant="primary"
                    size="md"
                    onClick={handleStart}
                    disabled={loadingId === breathwork.id || upgradeLoading}
                  >
                    {loadingId === breathwork.id || upgradeLoading ? t('detail.loading') : t('detail.startSession')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="primary"
                  size="md"
                  onClick={() => openLoginModal(location.pathname)}
                >
                  {t('detail.signInToStart')}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
